import { useEffect, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Trophy, TrendingUp, Wallet, ShoppingBag, Receipt, ShieldAlert, Medal, List } from "lucide-react";
import { toast } from "sonner";
import { ListeVentesTab } from "./ListeVentesTab";

const PERIODES = [
  { value: "jour", label: "Aujourd'hui" },
  { value: "semaine", label: "Cette semaine" },
  { value: "mois", label: "Ce mois-ci" },
  { value: "annee", label: "Cette année" },
  { value: "tout", label: "Depuis toujours" },
];

function Kpi({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={accent || "text-primary"} size={20} />
      </div>
      <p className="text-2xl font-heading font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

export default function StatistiquesPage() {
  const { user } = useAuth();
  const [periode, setPeriode] = useState("mois");
  const [customDebut, setCustomDebut] = useState("");
  const [customFin, setCustomFin] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const allowed = hasPerm(user, "statistiques", "view");

  const load = async () => {
    setLoading(true);
    try {
      const params = useCustom && (customDebut || customFin)
        ? { debut: customDebut || undefined, fin: customFin || undefined }
        : { periode };
      const { data: res } = await api.get("/stats/ventes", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, allowed]);

  const applyCustom = () => {
    if (!customDebut && !customFin) {
      toast.error("Indiquez au moins une date");
      return;
    }
    setUseCustom(true);
    load();
  };

  const resetPreset = (p) => {
    setUseCustom(false);
    setCustomDebut("");
    setCustomFin("");
    setPeriode(p);
  };

  if (!allowed) {
    return (
      <Layout title="Statistiques">
        <div className="flex flex-col items-center justify-center py-24 text-center text-muted-foreground" data-testid="stats-forbidden">
          <ShieldAlert size={32} className="mb-2 opacity-60" />
          <p className="text-sm">Vous n'avez pas accès aux statistiques de vente.</p>
        </div>
      </Layout>
    );
  }

  const g = data?.global || { ca_ttc: 0, ca_ht: 0, cout_ht: 0, benefice: 0, nb_ventes: 0 };
  const marge = g.ca_ht > 0 ? (g.benefice / g.ca_ht) * 100 : 0;

  return (
    <Layout title="Statistiques">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {PERIODES.map((p) => (
          <Button
            key={p.value}
            size="sm"
            variant={!useCustom && periode === p.value ? "default" : "outline"}
            onClick={() => resetPreset(p.value)}
            data-testid={`stats-periode-${p.value}`}
          >
            {p.label}
          </Button>
        ))}
        <div className="flex items-center gap-1.5 ml-2">
          <Input type="date" className="w-36 h-8 text-xs" value={customDebut} onChange={(e) => setCustomDebut(e.target.value)} data-testid="stats-debut-input" />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" className="w-36 h-8 text-xs" value={customFin} onChange={(e) => setCustomFin(e.target.value)} data-testid="stats-fin-input" />
          <Button size="sm" variant="outline" onClick={applyCustom} data-testid="stats-custom-apply">Appliquer</Button>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="mb-4 flex w-full flex-nowrap justify-start overflow-x-auto h-auto">
          <TabsTrigger value="dashboard" className="shrink-0" data-testid="stats-tab-dashboard">Tableau de bord</TabsTrigger>
          {user?.is_admin && (
            <TabsTrigger value="liste" className="gap-1.5 shrink-0" data-testid="stats-tab-liste">
              <List size={14} /> Liste des ventes
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="dashboard">
          {loading && !data ? (
            <p className="text-sm text-muted-foreground">Chargement...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Kpi icon={TrendingUp} label="Chiffre d'affaires généré (TTC)" value={`${g.ca_ttc.toFixed(2)} €`} testId="stats-kpi-ca" />
                <Kpi icon={ShoppingBag} label="Coût d'achat fournisseur (H.T)" value={`${g.cout_ht.toFixed(2)} €`} accent="text-muted-foreground" testId="stats-kpi-cout" />
                <Kpi icon={Wallet} label="Bénéfice réel (H.T)" value={`${g.benefice.toFixed(2)} €`} accent="text-emerald-600" testId="stats-kpi-benefice" />
                <Kpi icon={Receipt} label="Ventes (marge moyenne)" value={`${g.nb_ventes} · ${marge.toFixed(1)}%`} testId="stats-kpi-nb" />
              </div>

              {data?.meilleur_vendeur ? (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 mb-6 flex items-center gap-4" data-testid="stats-meilleur-vendeur">
                  <Trophy className="text-amber-500 shrink-0" size={32} />
                  <div>
                    <p className="text-xs text-muted-foreground">Meilleur vendeur de la période</p>
                    <p className="text-lg font-heading font-bold">{data.meilleur_vendeur.nom}</p>
                    <p className="text-sm text-muted-foreground">
                      {data.meilleur_vendeur.ca_ttc.toFixed(2)} € générés · {data.meilleur_vendeur.benefice.toFixed(2)} € de bénéfice · {data.meilleur_vendeur.nb_ventes} vente(s)
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-6" data-testid="stats-no-data">Aucune vente sur cette période.</p>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="font-heading font-semibold mb-3">Classement des vendeurs</h3>
                  <div className="space-y-2" data-testid="stats-vendeurs-list">
                    {(data?.vendeurs || []).length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
                    {(data?.vendeurs || []).map((v, idx) => (
                      <div key={v.vendeur_id} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg border border-border" data-testid={`stats-vendeur-row-${v.vendeur_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {idx === 0 ? <Medal size={15} className="text-amber-500 shrink-0" /> : <span className="w-[15px] text-center text-xs text-muted-foreground shrink-0">{idx + 1}</span>}
                          <div className="min-w-0">
                            <p className="font-medium truncate">{v.nom}</p>
                            <p className="text-xs text-muted-foreground">{v.nb_ventes} vente(s) · {v.nb_articles_vendus} article(s)</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold">{v.ca_ttc.toFixed(2)} €</p>
                          <p className="text-xs text-emerald-600">+{v.benefice.toFixed(2)} €</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="font-heading font-semibold mb-3">Articles les plus vendus</h3>
                  <div className="space-y-2" data-testid="stats-articles-list">
                    {(data?.top_articles || []).length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
                    {(data?.top_articles || []).map((a, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg border border-border" data-testid={`stats-article-row-${idx}`}>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{a.nom}</p>
                          <p className="text-xs text-muted-foreground">{a.qty} vendu(s)</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold">{a.ca_ttc.toFixed(2)} €</p>
                          <p className="text-xs text-emerald-600">+{a.benefice.toFixed(2)} €</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {user?.is_admin && (
          <TabsContent value="liste">
            <ListeVentesTab periode={periode} customDebut={customDebut} customFin={customFin} useCustom={useCustom} />
          </TabsContent>
        )}
      </Tabs>
    </Layout>
  );
}
